import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  BookOpen, Users, Plus, Search, Calendar, Bell, 
  GraduationCap, Briefcase, ArrowRight, Clock, FileText, ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { UserHeader } from '../../components/UserHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CampusDashboard() {
  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const navigate = useNavigate();
  
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;

  useEffect(() => {
    fetchEspacios();
  }, []);

  const fetchEspacios = async () => {
    try {
      const response = await axios.get(`${API_URL}/campus/espacios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEspacios(response.data);
    } catch (err) {
      console.error('Error cargando espacios:', err);
    } finally {
      setLoading(false);
    }
  };

  const espaciosFiltrados = espacios.filter(e => {
    const matchSearch = e.nombre?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchTipo = filtroTipo === 'todos' || e.tipo === filtroTipo;
    return matchSearch && matchTipo;
  });

  const cursos = espaciosFiltrados.filter(e => e.tipo === 'curso');
  const comisiones = espaciosFiltrados.filter(e => e.tipo === 'comision');

  // Obtener rol del usuario (admin, superadmin, etc.)
  const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin' || user?.rol === 'administracion';

  return (
    <div className="min-h-screen bg-gray-50">
      <UserHeader />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-sm text-white/70 hover:text-white mb-3 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <GraduationCap className="w-8 h-8" />
                Campus CATSLZ
              </h1>
              <p className="text-violet-200 mt-1">
                Tu espacio de aprendizaje y trabajo colaborativo
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {isAdmin && (
                <Button
                  onClick={() => navigate('/admin/campus')}
                  variant="secondary"
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  Administrar
                </Button>
              )}
              <Button
                onClick={() => navigate('/campus/unirse')}
                className="bg-white text-violet-700 hover:bg-violet-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Unirse con código
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Barra de búsqueda y filtros */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar espacios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filtroTipo === 'todos' ? 'default' : 'outline'}
              onClick={() => setFiltroTipo('todos')}
              size="sm"
            >
              Todos
            </Button>
            <Button
              variant={filtroTipo === 'curso' ? 'default' : 'outline'}
              onClick={() => setFiltroTipo('curso')}
              size="sm"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Cursos
            </Button>
            <Button
              variant={filtroTipo === 'comision' ? 'default' : 'outline'}
              onClick={() => setFiltroTipo('comision')}
              size="sm"
            >
              <Briefcase className="w-4 h-4 mr-1" />
              Comisiones
            </Button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
          </div>
        )}

        {/* Sin espacios */}
        {!loading && espacios.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Todavía no tenés espacios
              </h3>
              <p className="text-gray-500 mb-4">
                Unite a un curso o comisión con un código de invitación
              </p>
              <Button onClick={() => navigate('/campus/unirse')}>
                <Plus className="w-4 h-4 mr-2" />
                Unirse con código
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Cursos */}
        {!loading && cursos.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-violet-600" />
              Mis Cursos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cursos.map(espacio => (
                <EspacioCard key={espacio.id} espacio={espacio} />
              ))}
            </div>
          </section>
        )}

        {/* Comisiones */}
        {!loading && comisiones.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-emerald-600" />
              Mis Comisiones
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {comisiones.map(espacio => (
                <EspacioCard key={espacio.id} espacio={espacio} />
              ))}
            </div>
          </section>
        )}

        {/* Sin resultados de búsqueda */}
        {!loading && espacios.length > 0 && espaciosFiltrados.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No se encontraron espacios</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Componente de tarjeta de espacio
function EspacioCard({ espacio }) {
  const navigate = useNavigate();
  
  const colorClasses = {
    '#7c3aed': 'from-violet-500 to-purple-600',
    '#059669': 'from-emerald-500 to-teal-600',
    '#dc2626': 'from-red-500 to-rose-600',
    '#2563eb': 'from-blue-500 to-indigo-600',
    '#d97706': 'from-amber-500 to-orange-600',
  };
  
  const gradientClass = colorClasses[espacio.color_tema] || 'from-violet-500 to-purple-600';
  
  return (
    <Card 
      className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
      onClick={() => navigate(`/campus/espacio/${espacio.id}`)}
    >
      {/* Banner */}
      <div className={`h-24 bg-gradient-to-r ${gradientClass} relative`}>
        {espacio.imagen_portada ? (
          <img 
            src={espacio.imagen_portada} 
            alt="" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {espacio.tipo === 'curso' ? (
              <BookOpen className="w-12 h-12 text-white/30" />
            ) : (
              <Briefcase className="w-12 h-12 text-white/30" />
            )}
          </div>
        )}
        
        {/* Badge de rol */}
        {espacio.acceso_admin ? (
          <Badge className="absolute top-2 right-2 bg-amber-500 text-white" data-testid="badge-acceso-admin">
            Admin
          </Badge>
        ) : (espacio.mi_rol && espacio.mi_rol !== 'estudiante' && (
          <Badge className="absolute top-2 right-2 bg-white/90 text-gray-700">
            {espacio.mi_rol === 'coordinador' ? 'Coordinador/a' : 'Docente'}
          </Badge>
        ))}
      </div>
      
      {/* Contenido */}
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate group-hover:text-violet-600 transition-colors">
              {espacio.nombre}
            </h3>
            {espacio.descripcion && (
              <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                {espacio.descripcion}
              </p>
            )}
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0 ml-2" />
        </div>
        
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {espacio.total_miembros || 0}
          </span>
          <Badge variant="outline" className="text-xs">
            {espacio.tipo === 'curso' ? 'Curso' : 'Comisión'}
          </Badge>
          {espacio.modalidad && (
            <span className="capitalize">{espacio.modalidad}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
